import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UmsService } from '../ums.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-exam-dashboard',
  templateUrl: './exam-dashboard.component.html',
  styleUrls: ['./exam-dashboard.component.scss']
})
export class ExamDashboardComponent implements OnInit {

  sessionName: string = '';
  sessionNo: number;
  studentName: string = '';
  studentId: string;
  emid: any;
  examRows: Array<{
    emId: any;
    coCode: string;
    cgCode: string;
    examName: string;
    courseName: string;
    collegeName: string;
    disabled: boolean;
    session: string;
    sessionDisplayName: string; 
  }> = [];

  subjectSelectionData: any[] = [];
  hasSelectedSubjectsForCurrentSession: boolean = false;
  sessionMap: Map<string, string> = new Map(); 

  private pendingRequests = 0;

  constructor(
    private umsService: UmsService,
    private spinner: NgxSpinnerService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.studentId = sessionStorage.getItem('StudentPersonalId');
    this.studentName = sessionStorage.getItem('StudentName') || '';
    this.emid = 0;

    this.loadDashboardData();
  }

  loadDashboardData() {
    this.spinner.show();
    const payload = {};
    this.umsService.getSecretData().subscribe(
      (res: any) => {
        if (res.status) {
          this.sessionNo = res.projectPharam['SnNo'];
          this.pendingRequests = 1;
          this.getSessionName(this.sessionNo);
          if (this.studentId) {
            this.fetchSubjectSelection(this.studentId, this.emid);
          } else {
            this.buildFallbackRow();
            this.getCourseName(this.getFallbackCourseCode());
            this.getCollegeName(this.getFallbackCollegeCode());
          }
        } else {
          this.spinner.hide();
        }
      },
      (ERROR: HttpErrorResponse) => {
        this.spinner.hide();
      }
    );
  }

  private buildFallbackRow() {
    const defaultEmId = this.emid || '0';
    const sessionDisplayName = this.sessionMap.get(this.sessionNo?.toString()) || this.sessionNo?.toString() || '';
    this.examRows = [{
      emId: defaultEmId,
      coCode: this.getFallbackCourseCode(),
      cgCode: this.getFallbackCollegeCode(),
      examName: '',
      courseName: '',
      collegeName: '',
      disabled: false,
      session: this.sessionNo ? this.sessionNo.toString() : '',
      sessionDisplayName: sessionDisplayName
    }];
    this.hasSelectedSubjectsForCurrentSession = false;
  }

  private getFallbackCourseCode(): string {
    return sessionStorage.getItem('StuCoId') || '';
  }

  private getFallbackCollegeCode(): string {
    return sessionStorage.getItem('StuCgId') || '';
  }

  private requestCompleted() {
    this.pendingRequests--;
    if (this.pendingRequests === 0) {
      this.spinner.hide();
    }
  }

  getSessionName(snNo: number) {
    const payload = {};
    payload['SnNo'] = snNo;
    this.umsService.getSession(payload).subscribe(
      (res: any) => {
        if (res.status && res.data?.length) {
          this.sessionName = res.data[0].SnName;
          this.sessionMap.set(snNo.toString(), res.data[0].SnName);
        }
        this.requestCompleted();
      },
      (ERROR: HttpErrorResponse) => {
        this.requestCompleted();
      }
    );
  }

  fetchSessionName(sessionNo: string): Promise<string> {
    return new Promise((resolve) => {
      if (this.sessionMap.has(sessionNo)) {
        resolve(this.sessionMap.get(sessionNo));
        return;
      }

      const payload = {};
      payload['SnNo'] = parseInt(sessionNo);
      this.umsService.getSession(payload).subscribe(
        (res: any) => {
          if (res.status && res.data?.length) {
            const name = res.data[0].SnName;
            this.sessionMap.set(sessionNo, name);
            resolve(name);
          } else {
            resolve(sessionNo); // Fallback to session number
          }
        },
        (ERROR: HttpErrorResponse) => {
          resolve(sessionNo); // Fallback to session number
        }
      );
    });
  }

  fetchSubjectSelection(stuId: string, emid: string) {
    const emidToSend = emid || '0';
    const payload = {};
    payload['StuId'] = stuId;
    payload['emid'] = emidToSend;
    this.umsService.StudentSubjectSelectionById(payload).subscribe(
      (res: any) => {
        if (res.status && res.data?.length) {
          this.subjectSelectionData = res.data;

          const examMap = new Map();
          const uniqueSessions = new Set<string>();
          
          res.data.forEach((item: any) => {
            const sessionNo = item['SubSession'];
            uniqueSessions.add(sessionNo);
            const key = item['SubEmId'] + '_' + sessionNo;
            if (!examMap.has(key)) {
              examMap.set(key, {
                emId: item['SubEmId'] || '0',
                coCode: item['SubCoId'],
                cgCode: item['SubCgId'],
                examName: '',
                courseName: '',
                collegeName: '',
                disabled: item['Disabled'] === true,
                session: sessionNo,
                sessionDisplayName: sessionNo // Will be updated after fetching names
              });
            }
          });

          const fetchPromises = Array.from(uniqueSessions).map(sessionNo => 
            this.fetchSessionName(sessionNo)
          );

          Promise.all(fetchPromises).then((sessionNames) => {
            const rows = Array.from(examMap.values());
            rows.forEach(row => {
              const name = this.sessionMap.get(row.session);
              row.sessionDisplayName = name || row.session;
            });

            this.examRows = rows;
            this.hasSelectedSubjectsForCurrentSession = this.examRows.length > 0;
            
            this.pendingRequests += this.examRows.length * 3;
            this.examRows.forEach(row => {
              this.getCourseNameForRow(row);
              this.getCollegeNameForRow(row);
              this.getExamNameForRow(row);
            });
          });

        } else {
          this.hasSelectedSubjectsForCurrentSession = false;
          this.buildFallbackRow();
          this.pendingRequests += 2;
          this.getCourseName(this.getFallbackCourseCode());
          this.getCollegeName(this.getFallbackCollegeCode());
        }
        this.requestCompleted();
      },
      (ERROR: HttpErrorResponse) => {
        this.hasSelectedSubjectsForCurrentSession = false;
        this.buildFallbackRow();
        this.pendingRequests += 2;
        this.getCourseName(this.getFallbackCourseCode());
        this.getCollegeName(this.getFallbackCollegeCode());
        this.requestCompleted();
      }
    );
  }

  getCourseNameForRow(row: any) {
    const payload = {};
    payload['CoCode'] = row.coCode;
    this.umsService.fetchCourseType(payload).subscribe(
      (res: any) => {
        if (res.status && res.data?.length) {
          row.courseName = res.data[0].CoName;
        }
        this.requestCompleted();
      },
      (ERROR: HttpErrorResponse) => {
        this.requestCompleted();
      }
    );
  }

  getCollegeNameForRow(row: any) {
    const payload = {};
    payload['CgCode'] = row.cgCode;
    this.umsService.getCollege(payload).subscribe(
      (res: any) => {
        if (res.status && res.data?.length) {
          row.collegeName = res.data[0].CgName;
        }
        this.requestCompleted();
      },
      (ERROR: HttpErrorResponse) => {
        this.requestCompleted();
      }
    );
  }

  getExamNameForRow(row: any) {
    const payload = {};
    payload['EmCoCode'] = row.coCode;
    payload['EmId'] = row.emId;
    this.umsService.fetchExam(payload).subscribe(
      (res: any) => {
        if (res.status && res.data?.length) {
          row.examName = res.data[0].EmName;
        }
        this.requestCompleted();
      },
      (ERROR: HttpErrorResponse) => {
        this.requestCompleted();
      }
    );
  }

  getCourseName(coCode: string) {
    const payload = {};
    payload['CoCode'] = coCode;
    this.umsService.fetchCourseType(payload).subscribe(
      (res: any) => {
        if (res.status && res.data?.length && this.examRows.length > 0) {
          this.examRows[0].courseName = res.data[0].CoName;
        }
        this.requestCompleted();
      },
      (ERROR: HttpErrorResponse) => {
        this.requestCompleted();
      }
    );
  }

  getCollegeName(cgCode: string) {
    const payload = {};
    payload['CgCode'] = cgCode;
    this.umsService.getCollege(payload).subscribe(
      (res: any) => {
        if (res.status && res.data?.length && this.examRows.length > 0) {
          this.examRows[0].collegeName = res.data[0].CgName;
        }
        this.requestCompleted();
      },
      (ERROR: HttpErrorResponse) => {
        this.requestCompleted();
      }
    );
  }

  goToExamForm(emId: string, coCode: string, cgCode: string, session: string) {
    const emidParam = emId || '0';
    this.router.navigate(['/Exam-Form'], {
      queryParams: {
        emid: emidParam,
        courseCode: coCode,
        collegeCode: cgCode,
        sessionNo: session,
        studentId: this.studentId
      }
    });
  }

  goToExamFee(emId: string, coCode: string, cgCode: string, session: string) {
    const emidParam = emId || '0';
    this.router.navigate(['/Exam-Fees'], {
      queryParams: {
        emid: emidParam,
        courseCode: coCode,
        collegeCode: cgCode,
        sessionNo: session,
        studentId: this.studentId
      }
    });
  }
}